/**
 * Workflow Migration Utilities
 * 
 * Handles migration of old workflow formats to new formats
 */

import type { Node } from 'reactflow';
import { getNodeSchema } from './node-registry';

/**
 * Reserved keys that should NOT be moved to parameters
 */
const RESERVED_KEYS = new Set([
  'label',
  'category',
  'nodeType',
  'config',
  'parameters', // Already in correct location
  'executionOutput', // Runtime data
  'continueOnFail', // Workflow settings
  'retryOnFail', // Workflow settings
  'timeout', // Workflow settings
  'notes', // Workflow settings
]);

/**
 * Migrate old node data format to new format
 * 
 * Old format: Properties stored directly in data
 * New format: Properties stored in data.parameters
 */
export function migrateNodeData(node: Node): Node {
  const { data } = node;
  
  if (!data) {
    return node;
  }

  // If parameters already exist and have content, assume it's already migrated
  if (data.parameters && Object.keys(data.parameters).length > 0) {
    return node;
  }

  // Get the node schema to know which properties are valid
  const schema = getNodeSchema(node.type || '');
  const validPropertyKeys = new Set(
    schema.map((prop: any) => prop.key || prop.name).filter(Boolean)
  );

  // Separate reserved keys from property keys
  const parameters: Record<string, any> = {};
  const reservedData: Record<string, any> = {};

  for (const [key, value] of Object.entries(data)) {
    if (RESERVED_KEYS.has(key)) {
      // Keep reserved keys at top level
      reservedData[key] = value;
    } else if (validPropertyKeys.has(key)) {
      // Move valid properties to parameters
      parameters[key] = value;
    } else {
      // Unknown keys - keep at top level for safety
      reservedData[key] = value;
    }
  }

  // Return migrated node
  return {
    ...node,
    data: {
      ...reservedData,
      parameters: Object.keys(parameters).length > 0 ? parameters : {},
    },
  };
}

/**
 * Migrate an entire workflow definition
 */
export function migrateWorkflowDefinition(definition: any): any {
  if (!definition || !definition.nodes) {
    return definition;
  }

  return {
    ...definition,
    nodes: definition.nodes.map(migrateNodeData),
  };
}

/**
 * Migrate AI Agent nodes from old structure to new structure
 * 
 * Old: AI Agent has provider, model, credentialId, temperature, maxTokens
 * New: AI Agent only has prompts, LLM config is in sub-node
 */
export function migrateAIAgentNode(node: Node): Node {
  if (node.type !== 'ai-agent') {
    return node;
  }

  const { data } = node;
  if (!data) {
    return node;
  }

  // Check if this node has old fields
  const hasOldFields = 
    data.provider ||
    data.model ||
    data.credentialId ||
    data.temperature !== undefined ||
    data.maxTokens !== undefined;

  if (!hasOldFields) {
    return node; // Already migrated
  }

  // Extract valid AI Agent fields
  const newData: Record<string, any> = {
    label: data.label,
    category: data.category,
    nodeType: data.nodeType,
    parameters: {
      systemPrompt: data.systemPrompt || data.parameters?.systemPrompt,
      userPrompt: data.userPrompt || data.parameters?.userPrompt,
      responseStyle: data.responseStyle || data.parameters?.responseStyle || 'concise',
      maxIterations: data.maxIterations || data.parameters?.maxIterations || 10,
      requiresAction: data.requiresAction || data.parameters?.requiresAction || false,
      returnIntermediateSteps: data.returnIntermediateSteps || data.parameters?.returnIntermediateSteps || false,
      outputKey: data.outputKey || data.parameters?.outputKey || 'output',
    },
  };

  // Preserve workflow settings
  if (data.continueOnFail !== undefined) newData.continueOnFail = data.continueOnFail;
  if (data.retryOnFail !== undefined) newData.retryOnFail = data.retryOnFail;
  if (data.timeout !== undefined) newData.timeout = data.timeout;
  if (data.notes !== undefined) newData.notes = data.notes;

  return {
    ...node,
    data: newData,
  };
}

/**
 * Replace old claudeChatModel nodes with anthropic nodes
 */
export function migrateClaudeChatModelNode(node: Node): Node {
  if (node.type !== 'claudeChatModel') {
    return node;
  }

  return {
    ...node,
    type: 'anthropic',
    data: {
      ...node.data,
      label: 'Anthropic Claude',
      category: 'ai-nodes',
      nodeType: 'anthropic',
      parameters: {
        credentialId: node.data?.credentialId || node.data?.parameters?.credentialId || '',
        model: node.data?.model || node.data?.parameters?.model || 'claude-3-5-sonnet-20241022',
        temperature: node.data?.temperature ?? node.data?.parameters?.temperature ?? 0.7,
        maxTokens: node.data?.maxTokens ?? node.data?.parameters?.maxTokens ?? 4096,
        topP: node.data?.topP ?? node.data?.parameters?.topP ?? 1,
      },
    },
  };
}

/**
 * Migrate all nodes in a workflow
 */
export function migrateAllNodes(nodes: Node[]): Node[] {
  return nodes.map((node) => {
    // First, migrate old claudeChatModel to anthropic
    let migratedNode = migrateClaudeChatModelNode(node);
    
    // Then, migrate AI Agent if needed
    migratedNode = migrateAIAgentNode(migratedNode);
    
    // Finally, migrate general node data structure
    migratedNode = migrateNodeData(migratedNode);
    
    return migratedNode;
  });
}

/**
 * Check if a workflow needs migration
 */
export function needsMigration(definition: any): boolean {
  if (!definition || !definition.nodes) {
    return false;
  }

  return definition.nodes.some((node: Node) => {
    // Check for old claudeChatModel type
    if (node.type === 'claudeChatModel') {
      return true;
    }

    // Check for AI Agent with old fields
    if (node.type === 'ai-agent' && node.data) {
      if (
        node.data.provider ||
        node.data.model ||
        node.data.credentialId ||
        node.data.temperature !== undefined ||
        node.data.maxTokens !== undefined
      ) {
        return true;
      }
    }

    // Check for properties stored directly in data (not in parameters)
    if (node.data && !node.data.parameters) {
      const schema = getNodeSchema(node.type || '');
      const validPropertyKeys = new Set(
        schema.map((prop: any) => prop.key || prop.name).filter(Boolean)
      );

      // If any valid property key exists directly in data, needs migration
      for (const key of Object.keys(node.data)) {
        if (validPropertyKeys.has(key) && !RESERVED_KEYS.has(key)) {
          return true;
        }
      }
    }

    return false;
  });
}
